import cn from 'classnames'
import React, { memo, useCallback, useEffect, useMemo } from 'react'

import { Content } from './components/content/Content'
import { HorizontalMenuItem } from './components/horizontalMenuItem/HorizontalMenuItem'
import { Menu } from './components/menu/Menu'
import { isHorizontalMenuItem } from './helpers/isHorizontalMenuItem'
import { IHorizontalMenuProps } from './models'

import styles from './HorizontalMenu.module.scss'

export const HorizontalMenu = memo<IHorizontalMenuProps>(({ children, activeTab = 0 }) => {
  const [activeIndex, setActiveIndex] = React.useState(activeTab)

  const validChildren = useMemo(
    () => React.Children.toArray(children).filter(isHorizontalMenuItem),
    [children]
  )

  const menuItemMapping: number[] = useMemo(
    () =>
      validChildren.reduce<number[]>((acc, child, index) => {
        if (isHorizontalMenuItem(child)) {
          acc.push(index)
        }
        return acc
      }, []),
    [validChildren]
  )

  const handleMenuItemClick = useCallback(
    (index: number) => {
      const child = validChildren[index]
      if (isHorizontalMenuItem(child)) {
        setActiveIndex(index)
      }
    },
    [validChildren]
  )

  useEffect(() => {
    if (activeTab) {
      const innerIndex = menuItemMapping[activeTab]
      setActiveIndex(innerIndex)
    }
  }, [menuItemMapping, activeTab])

  return (
    <div className={cn(styles.container)}>
      <Menu
        activeIndex={activeIndex}
        onMenuItemClick={handleMenuItemClick}
        validChildren={validChildren}
      />
      <Content activeIndex={activeIndex} validChildren={validChildren} />
    </div>
  )
}) as React.NamedExoticComponent<IHorizontalMenuProps> & {
  Item: typeof HorizontalMenuItem
}

HorizontalMenu.Item = HorizontalMenuItem
